import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import _get from 'lodash/get';
import { createCustomFetch } from '@lib/utils/customFetch';
import isAbortError from '@lib/utils/isAbortError';
import { createLoggingLink } from '@lib/services/logger';
import { hasAuthenticationError } from '@lib/utils/authErrorHandler';
import {
  hasTechnicalPauseError,
  handleTechnicalPauseError,
} from '@lib/utils/technicalPauseErrorHandler';
import { handleGraphQLErrors } from '@lib/utils/versionMismatchErrorHandler';
import { captureException } from '@lib/utils/captureException';
import { emitSessionExpired } from '@lib/utils/sessionExpiredEvent';

const { VITE_API_URL: API_DOMAIN, VITE_AUTH_SCOPE, MODE } = import.meta.env;

if (MODE && ['development', 'test'].includes(MODE)) {
  loadDevMessages();
  loadErrorMessages();
}

const customHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-Auth-Scope': VITE_AUTH_SCOPE,
  'Cache-Control': 'no-cache',
};

const cors = {
  xhrFields: { withCredentials: true },
  crossDomain: true,
  mode: 'cors',
  credentials: 'include',
};

const singleHttpLink = new HttpLink({
  uri: `${API_DOMAIN}/graphql`,
  headers: customHeaders,
  fetch: createCustomFetch,
  ...cors,
});

const httpLink = new BatchHttpLink({
  uri: `${API_DOMAIN}/graphql`,
  batchMax: 5,
  batchInterval: 20,
  headers: customHeaders,
  fetch: createCustomFetch,
  ...cors,
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (hasTechnicalPauseError(graphQLErrors)) {
    handleTechnicalPauseError(graphQLErrors);
  }

  if (
    hasAuthenticationError(graphQLErrors) ||
    _get(networkError, 'statusCode') === 401
  ) {
    emitSessionExpired();
    return;
  }

  handleGraphQLErrors(graphQLErrors, captureException);

  const statusCode = _get(networkError, 'statusCode');
  // AbortError here means the request was cancelled deliberately (component
  // unmount, a newer query superseding this one, page navigation) - not a
  // real failure.
  if (networkError && statusCode !== 429 && !isAbortError(networkError)) {
    captureException(networkError);
  }
});

let hasFetchedToken = false;
const optionsRequestLink = new ApolloLink((operation, forward) => {
  if (hasFetchedToken) {
    // If token has been fetched, proceed with the operation
    return forward(operation);
  }
  return new Observable((observer) => {
    // Send an OPTIONS request
    fetch('/graphql', {
      method: 'OPTIONS',
      credentials: 'include',
    })
      .then(() => {
        hasFetchedToken = true;
      })
      .finally(() => {
        // Proceed with the original request
        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      })
      .catch((error) => {
        observer.error(error);
      });
  });
});

const sentryTracingLink = new ApolloLink((operation, forward) => {
  return Sentry.startSpan(
    { name: operation.operationName || 'GraphQL Request', op: 'graphql' },
    (span) => {
      return forward(operation).map((response) => {
        span?.end();
        return response;
      });
    },
  );
});

const customLink = new ApolloLink((operation, forward) => {
  const { operationName } = operation;
  return ['Whoami', 'siteLocationCenter', 'sectorLocationCenter'].includes(
    operationName,
  )
    ? singleHttpLink.request(operation, forward)
    : httpLink.request(operation);
});

const loggingLink = createLoggingLink();

const link = from([
  loggingLink,
  sentryTracingLink,
  optionsRequestLink,
  errorLink,
  customLink,
]);

const Client = new ApolloClient({
  cache: new InMemoryCache(),
  link,
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  },
});

export default Client;
